import { FastField, FastFieldProps, FormikHandlers } from "formik";
import { CSSProperties } from "react";
import styled from "styled-components";
import { ApiErrorResponse } from "../../api-utils/errors";
import { pxToRem } from "@input-output-hk/px-to-rem";
import { forTabletPortraitUp } from "../../tokens/media-queries";
import { FIELD_LABELS } from "../contribute/form/ContributeForm";
import { shouldUpdateFastField } from "../contribute/form/ContributeForm.helpers";
import ImageUploadField from "./ImageUploadField";
import TextField from "../contribute/TextField";

const Fieldset = styled.fieldset`
  appearance: none;
  border: 0;
  padding: 0;

  [data-upload-image] {
    min-height: ${pxToRem(138)};
  }

  display: grid;

  @media ${forTabletPortraitUp} {
    grid-template-columns: 1fr 2fr;
    grid-template-rows: 1fr 1fr;
    grid-gap: var(--spacing-large);

    [data-upload-image] {
      grid-column: 1 / 2;
      grid-row: 1 / 3;
    }
    [data-image-url] {
      grid-column: 2 / 3;
      grid-row: 1 / 2;
    }
    [data-image-alt] {
      grid-column: 2 / 3;
      grid-row: 2 / 3;
    }
  }
`;

type Props<T extends string> = {
  enabled: boolean;
  onUploadError: (err: ApiErrorResponse<string>) => void;
  id: string;
  name: T;
  className?: string;
  style?: CSSProperties;
} & Pick<FormikHandlers, "getFieldHelpers">;

export type ImageFieldNames<T extends string> = `${T}` | `${T}_text`;
export type ImageFields<T extends string> = Record<ImageFieldNames<T>, string>;

function UploadImageFieldset<T extends string>({
  id,
  name,
  enabled,
  onUploadError,
  getFieldHelpers,
  className,
  style
}: Props<T>): JSX.Element {
  const urlFieldName: ImageFieldNames<T> = `${name}`;
  const altFieldName: ImageFieldNames<T> = `${name}_text`;
  return (
    <Fieldset className={className} style={style}>
      <FastField
        name={urlFieldName}
        disabled={!enabled}
        shouldUpdate={shouldUpdateFastField}
      >
        {(props: FastFieldProps) => (
          <ImageUploadField
            id={`${id}-upload`}
            label="Image"
            name={props.field.name}
            disabled={!enabled}
            getFieldHelpers={getFieldHelpers}
            onUploadError={onUploadError}
            {...props}
          />
        )}
      </FastField>
      <FastField
        name={urlFieldName}
        disabled={!enabled}
        shouldUpdate={shouldUpdateFastField}
      >
        {(props: FastFieldProps) => (
          <TextField
            outerProps={{ "data-image-url": true }}
            label="Image URL"
            name={props.field.name}
            id={id}
            placeholder="e.g. https://example.com/image.jpg"
            disabled={!enabled}
            {...props}
          />
        )}
      </FastField>
      <FastField
        name={altFieldName}
        disabled={!enabled}
        shouldUpdate={shouldUpdateFastField}
      >
        {(props: FastFieldProps) => (
          <TextField
            outerProps={{ "data-image-alt": true }}
            label={FIELD_LABELS["image_text"]}
            name={props.field.name}
            id={`${id}_text`}
            placeholder="e.g. A picture about Cardano"
            disabled={!enabled}
            {...props}
          />
        )}
      </FastField>
    </Fieldset>
  );
}

export default UploadImageFieldset;
